import { Log } from '@/parsing/Log';

import { generateAndroidAnalyticsGroupFromLog } from './generators/android/generateAndroidAnalyticsGroupFromLog';
import { generateAndroidAppStateGroupFromLog } from './generators/android/generateAndroidAppStateGroupFromLog';
import { generateAndroidRouterGroupFromLog } from './generators/android/generateAndroidRouterGroupFromLog';
import { generateGpsHistoryGroupFromLog } from './generators/generic/generateGpsHistoryGroupFromLog';
import { generateMQTTGroupFromLog } from './generators/generic/generateMQTTGroupFromLog';
import { generateNetworkGroupFromLog } from './generators/generic/generateNetworkGroupFromLog';
import { generateIosAnalyticsGroupFromLog } from './generators/ios/generateIosAnalyticsGroupFromLog';
import { generateIosAppEventsGroupFromLog } from './generators/ios/generateIosAppEventsGroupFromLog';
import { generateIosAppStateGroupFromLog } from './generators/ios/generateIosAppStateGroupFromLog';
import { generateIosMapEventsGroupFromLog } from './generators/ios/generateIosMapEventsGroupFromLog';
import { generateIosRouterGroupFromLog } from './generators/ios/generateIosRouterGroupFromLog';
import { generateReduxActionGroupFromLog } from './generators/reactnative/generateReduxActionGroupFromLog';
import { TimelineGeneratorContext } from './generators/TimelineGeneratorContext';
import { TimelineData, TimelineRowGroup } from './TimelineData';
import { getTimelineSettings, TimelineSettings } from './TimelineSettings';

export abstract class TimelineDataGenerator {
  static generateFromLog(
    log: Log,
    convertLogEntryIndexToLineIndex: (logEntryIndex: number) => number | undefined,
    settings: TimelineSettings = getTimelineSettings(),
  ): TimelineData {
    const ctx: TimelineGeneratorContext = {
      nextMarkerId: 1,
      convertLogEntryIndexToLineIndex,
    };

    const rowGroupsFns = [
      generateIosAppStateGroupFromLog,
      generateAndroidAppStateGroupFromLog,

      generateNetworkGroupFromLog,
      generateMQTTGroupFromLog,

      generateGpsHistoryGroupFromLog,

      generateReduxActionGroupFromLog,

      generateIosAnalyticsGroupFromLog,
      generateAndroidAnalyticsGroupFromLog,
      generateIosMapEventsGroupFromLog,
      generateIosAppEventsGroupFromLog,
      generateIosRouterGroupFromLog,
      generateAndroidRouterGroupFromLog,
    ];

    const rowGroups: TimelineRowGroup[] = [];

    rowGroupsFns.forEach((fn) => {
      try {
        const group = fn(log, ctx);
        if (group.rows.length > 0) {
          const expanded = settings.isRowGroupExpanded(group.name);
          group.expanded = typeof expanded === 'boolean' ? expanded : true;
          rowGroups.push(group);
        }
      } catch (error) {
        console.error('Group generation failed', fn, error);
      }
    });

    const data: TimelineData = {
      logId: log.uniqueId,
      rowGroups,
      startTimeMs: 0,
      endTimeMs: 0,
    };

    data.rowGroups.forEach((group) => {
      group.rows.forEach((row) => {
        row.markers.forEach((marker) => {
          data.startTimeMs = data.startTimeMs
            ? Math.min(data.startTimeMs, marker.startTimeMs)
            : marker.startTimeMs;
          data.endTimeMs = data.endTimeMs
            ? Math.max(data.endTimeMs, marker.endTimeMs || marker.startTimeMs)
            : marker.endTimeMs || marker.startTimeMs;
        });
      });
    });

    return data;
  }
}
